/**
 * The page lens in the Kory tray: off Overview, the tray's up next follows the page. The page
 * publishes the records it shows (`useKoryNouns`); two seconds after they settle the tray asks
 * `POST /kory/feed/lens` for Kory's page suggestions (at most two) and the caller's items about
 * those records. Behind the New Overview gate. In the tray's focus mode (door 3) the tray takes the
 * page's items as its focus: it hears them through `onMatched`, a row picks instead of opening,
 * and the rows it already holds (`hidden`) aren't listed twice.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useDebounceValue } from "usehooks-ts";

import { KoryMadeMark } from "@/components/feed/feed-glyph";
import { FeedItem } from "@/components/feed/feed-item";
import { useRankedFeedGate } from "@/components/feed/use-ranked-feed-gate";
import type { FeedItem as KitItem, FeedTag } from "@/components/feed/types";
import { Button } from "@/components/ui/button";
import type { TodoDTO } from "@/hooks/use-agent-todos";
import { apiErrorMessage, fetchApi } from "@/lib/api";
import { glyphOf } from "@/lib/kory-feed";
import { useNounKey, usePageNouns, type PageNoun } from "@/lib/kory-nouns";
import { capturePage } from "@/lib/kory-page-context";
import { pageSharing, usePageSharing } from "@/lib/kory-page-sharing";
import { useKoryPreview, withKoryPreview } from "@/lib/kory-preview";

/** The nouns settle this long before the tray asks. */
const SETTLE_MS = 2000;
/** The server's cap on page text (context for wording only). */
const PAGE_TEXT_MAX = 8000;
/** How many of the caller's items about the page the tray lists. */
export const MATCHED_SHOWN = 5;

export type PageSuggestion = { id: string; title: string; why: string; nouns: PageNoun[]; tool?: string | null };

type LensFeedItem = {
  n: number;
  key: string;
  kind: string;
  tag: string;
  todo: TodoDTO | null;
  /** A strip's, check-in's or fold's to-dos. */
  members: TodoDTO[];
  reason: { code: string; text: string };
  pinned_by: "you" | "kory" | null;
  is_new: boolean;
  nouns: PageNoun[];
};

export type LensFeed = {
  items: LensFeedItem[];
  lens: {
    route: string;
    label: string;
    matched: number;
    suggestions: PageSuggestion[];
    /** The listed to-dos about the page; a grouped item's other members are not. */
    matched_keys?: string[];
    /** Shown when nothing in the feed is about the page. */
    empty?: string | null;
  } | null;
};

const WORK_TAGS = new Set<FeedTag>(["suggested", "approve", "todo", "update", "working", "failed"]);

/** A ranked item about the page as feed kit rows: one per to-do, so a grouped item lists its members about the page. */
function lensRows(item: LensFeedItem, matched: ReadonlySet<string>): KitItem[] {
  if (!WORK_TAGS.has(item.tag as FeedTag)) return [];
  const todos = item.todo ? [item.todo] : (item.members ?? []).filter((todo) => matched.has(todo.id));
  return todos.map((todo) => lensRow(item, todo));
}

function lensRow(item: LensFeedItem, todo: TodoDTO): KitItem {
  return {
    n: item.n,
    key: todo.id,
    kind: "row",
    // The item's nouns, so Kory naming one of them (a rock, a deal) shows the leaf on the row.
    nouns: item.nouns.map((noun) => ({ ...noun, label: noun.label ?? "" })),
    tag: item.tag as FeedTag,
    title: todo.title,
    todo,
    reason: item.reason,
    pinned_by: item.pinned_by,
    is_new: item.is_new,
    glyph: glyphOf({ tag: item.tag as FeedTag, is_new: item.is_new, reason: item.reason }, todo),
    size: "row",
  };
}

const LENS_KEY = ["kory", "feed", "lens"];
const NO_ANSWERS: ReadonlySet<string> = new Set();

function useLensFeed(route: string, nouns: PageNoun[], enabled: boolean, previewId: string | null) {
  const nounKey = useNounKey(nouns);
  return useQuery<LensFeed>({
    queryKey: [...LENS_KEY, route, nounKey, previewId ?? "self"],
    queryFn: ({ signal }) => {
      // Read at send time too: a refetch after the chip turned sharing off sends nothing.
      const sharing = pageSharing(route);
      if (sharing === "none") return Promise.resolve({ items: [], lens: null });
      const text = sharing === "page" ? (capturePage()?.text ?? "").slice(0, PAGE_TEXT_MAX) : "";
      const preview = withKoryPreview("/kory/feed/lens", previewId);
      return fetchApi(preview.url, {
        method: "POST",
        body: JSON.stringify({ route, nouns, page_text: text || null }),
        headers: preview.headers,
        signal,
      });
    },
    enabled: enabled && (nouns.length > 0 || route.replace(/\/$/, "") === "/mna/integrations"),
    staleTime: 5 * 60_000,
  });
}

export function PageLens({
  onOpen,
  onPick,
  onMatched,
  hidden,
  readOnly = false,
}: {
  onOpen: (todo: TodoDTO) => void;
  /** Focus mode: a row becomes the tray's focus. */
  onPick?: (todoKey: string) => void;
  /** Focus mode: the page's to-do keys, in the lens's order (empty when the page names none). */
  onMatched?: (keys: string[]) => void;
  /** Rows the tray already holds. */
  hidden?: ReadonlySet<string>;
  /** Preview keeps the lens visible but removes every suggestion write. */
  readOnly?: boolean;
}) {
  const { pathname } = useLocation();
  const preview = useKoryPreview();
  const previewing = preview !== null;
  const subjectKey = preview?.id ?? "self";
  const nouns = usePageNouns();
  // The route settles with its nouns: a new page never asks with the last page's records.
  const live = useMemo(() => ({ route: pathname, nouns }), [pathname, nouns]);
  const [settled] = useDebounceValue(live, SETTLE_MS);
  // When the This page chip shares nothing, the page's nouns are not sent either.
  const sharing = usePageSharing(pathname);
  const rankedFeed = useRankedFeedGate();
  const on = rankedFeed && sharing !== "none" && settled.route === pathname;
  const feed = useLensFeed(pathname, settled.nouns, on, preview?.id ?? null);
  const queryClient = useQueryClient();
  // A to-do completed, edited or accepted anywhere refreshes the lens with it.
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type === "updated" && event.action.type === "invalidate" && event.query.queryKey[0] === "agent-todos") {
          void queryClient.invalidateQueries({ queryKey: LENS_KEY });
        }
      }),
    [queryClient],
  );
  const [answeredBySubject, setAnsweredBySubject] = useState<Record<string, ReadonlySet<string>>>({});
  const answered = answeredBySubject[subjectKey] ?? NO_ANSWERS;

  const settle = (id: string) =>
    setAnsweredBySubject((prev) => ({ ...prev, [subjectKey]: new Set(prev[subjectKey] ?? NO_ANSWERS).add(id) }));
  const accept = useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/kory/feed/lens/${encodeURIComponent(id)}/accept`, { method: "POST", body: JSON.stringify({ route: pathname }) }),
    onSuccess: (body: { tool?: string | null } | undefined, id) => {
      settle(id);
      toast.success(body?.tool === "meeting_create_issue" ? "Added for your meeting" : "Added to your to-dos");
      void queryClient.invalidateQueries({ queryKey: ["agent-todos"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't add that. Try again in a moment.")),
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => fetchApi(`/kory/feed/lens/${encodeURIComponent(id)}/dismiss`, { method: "POST" }),
    onSuccess: (_, id) => settle(id),
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't hide that. Try again in a moment.")),
  });

  const lens = on ? feed.data?.lens : undefined;
  // Off (sharing turned off, or another page) the lens lists nothing and reports nothing.
  const allRows = useMemo(() => {
    if (!lens) return [];
    const matched = new Set(lens.matched_keys ?? []);
    return (feed.data?.items ?? [])
      .filter((item) => item.nouns.length > 0)
      .flatMap((item) => lensRows(item, matched))
      // The lens is its own short list: a group's members each get their own number.
      .map((row, i) => ({ ...row, n: i + 1 }));
  }, [feed.data, lens]);
  const matchedKey = allRows.map((row) => row.key).join("\n");
  useEffect(() => {
    onMatched?.(matchedKey ? matchedKey.split("\n") : []);
  }, [matchedKey, onMatched]);
  useEffect(() => () => onMatched?.([]), [onMatched]);
  if (!on || !lens) return null;
  const suggestions = lens.suggestions.filter((s) => !answered.has(s.id));
  const rows = hidden ? allRows.filter((row) => !hidden.has(row.key)) : allRows;
  const sentence = lens.matched === 0 ? lens.empty : null;
  if (!suggestions.length && !rows.length && !sentence) return null;

  return (
    <section aria-label={lens.label} data-testid="page-lens" className="space-y-1.5">
      <h3 className="px-1 text-xs font-medium text-muted-foreground">{lens.label}</h3>
      {sentence ? (
        <p data-testid="page-lens-empty" className="px-1 text-xs text-muted-foreground">
          {sentence}
        </p>
      ) : null}
      {suggestions.map((s) => {
        const busy = (accept.isPending && accept.variables === s.id) || (dismiss.isPending && dismiss.variables === s.id);
        return (
          <div key={s.id} data-page-suggestion={s.id} className="relative space-y-2 rounded-md border bg-card px-3 py-2.5" aria-busy={busy || undefined}>
            <div className="min-w-0 space-y-0.5">
              <p className="flex min-w-0 items-start gap-1.5 text-sm font-medium leading-5">
                <KoryMadeMark className="mt-0.5 size-3.5 shrink-0" />
                <span className="min-w-0">{s.title}</span>
              </p>
              <p className="text-xs leading-4 text-muted-foreground">{s.why}</p>
            </div>
            {readOnly || previewing ? null : (
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => accept.mutate(s.id)}>
                  {accept.isPending && accept.variables === s.id ? "Adding…" : "Accept"}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => dismiss.mutate(s.id)}>
                  Not now
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {rows.slice(0, MATCHED_SHOWN).map((row) => (
        <button
          key={row.key}
          type="button"
          onClick={() => (onPick ? onPick(row.key) : row.todo && onOpen(row.todo))}
          className="block w-full rounded-md text-left focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
        >
          <FeedItem item={row} numbered />
        </button>
      ))}
      {rows.length > MATCHED_SHOWN ? (
        <p className="px-1 text-xs text-muted-foreground">{rows.length - MATCHED_SHOWN} more about this page below.</p>
      ) : null}
    </section>
  );
}
